import { Component, computed, inject, Signal, Type, signal, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { SelectTimezoneDialogComponent } from '../select-timezone-dialog/select-timezone-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { GlobalTimezoneService } from '../../services/global-timezone.service';
import { MDMenuComponent, IMDMenuOption, MDDialogService, MDIconComponent } from '@melodic-ui/components';
import { MDPopupComponent } from '@melodic-ui/components';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { AuthenticationService } from '@lb/auth/services/authentication.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { UserContextService } from '@lb/state/user-context.service';
import { buildSupportTicketUrl } from '@lb/functions/support-ticket-url.function';
import { IUser } from '@lb/api/mylb/interfaces/iuser.interface';
import { ITimeZone } from '@lb/api/mylb/interfaces/itime-zone.interface';
import { IUserContextAssociatedUser } from '@lb/api/mylb/interfaces/iuser-context-associated-user.interface';

/** The user whose info is shown in the menu header (always the real logged-in person). */
interface IUserMenuHeaderUser {
	userID: number;
	/** Full display name ("First Last" or username when original admin). */
	name: string;
	email: string;
	tzDisplay?: string;
	showTzWarning: boolean;
	browserTzDisplay?: string;
}

/**
 * Describes a "viewing-as" or "impersonating" context.
 * When null the menu is in standard (own-account) mode.
 */
interface IUserMenuViewContext {
	/** 'impersonating' | 'guardian' | 'institution' */
	type: 'impersonating' | 'guardian' | 'institution';
	/** Whether the trigger should show stacked avatars (i.e. we\'re actively on a different account). */
	isViewingOther: boolean;
	/** The subject user shown in the trigger and the section card/row. */
	primaryUserID: number;
	primaryFirstName: string;
	primaryLastName: string;
	primaryEmail?: string;
	primaryTzDisplay?: string;
	/** Short label shown next to the stacked avatars in the trigger. */
	triggerLabel: string;
	/** The section heading ('Impersonating' | 'Switch Account' | 'Switch User'). */
	sectionTitle: string;
}

@Component({
	selector: 'lb-user-menu',
	standalone: true,
	imports: [CommonModule, UserIconComponent, MDMenuComponent, MDIconComponent],
	templateUrl: './user-menu.component.html',
	styleUrl: './user-menu.component.scss'
})
export class UserMenuComponent implements AfterViewChecked {
	@ViewChild(MDPopupComponent) private readonly _menuPopup?: MDPopupComponent;
	@ViewChild('switchList') private readonly _switchList?: ElementRef<HTMLElement>;

	/**
	 * Tracks list visibility across frames — `offsetParent` is null when
	 * any ancestor has display:none (i.e. the popup is closed).
	 */
	private _wasListVisible = false;
	private readonly _appConfig: IAppSettings = inject(APP_CONFIG);
	private readonly _accountStateService = inject(AccountStateService);
	private readonly _globalTimezoneService = inject(GlobalTimezoneService);
	private readonly _userContextService = inject(UserContextService);
	private readonly _dialogService = inject(MDDialogService);
	private readonly _authService = inject(AuthenticationService);
	private readonly _router = inject(Router);

	// Own-user identity
	public readonly user: Signal<IUser> = this._accountStateService.user;
	public readonly userID = computed(() => this.user().userID);
	public readonly isImpersonating = this._accountStateService.isImpersonating;
	public readonly isAdmin = computed(() => this.user()?.entitlements?.some((e) => e.entitlementName === 'Admin:App:Access') ?? false);

	// Timezone
	public readonly currentTzAbbrev = this._globalTimezoneService.selectedTimezoneAbbrev;
	public readonly tzMismatch = this._globalTimezoneService.tzMismatch;
	public readonly selectedTzDisplay = this._globalTimezoneService.selectedTimezoneDisplayLabel;
	public readonly browserTzDisplay = this._globalTimezoneService.browserTimezoneDisplayLabel;
	public readonly impersonatedTzDisplay = this._globalTimezoneService.learnerTimezoneDisplayLabel;
	// Suppress tz warning when actively viewing as another person
	public readonly showTzWarning = computed(() => this.tzMismatch() && !this.isImpersonating() && !this.isViewingOtherUser());

	// Viewing-as context
	public readonly switchMode = this._userContextService.switchMode;
	public readonly associatedUsers = this._userContextService.associatedUsers;
	public readonly viewingUserID = this._userContextService.viewingUserID;
	public readonly viewingUser = this._userContextService.viewingUser;
	public readonly isViewingOtherUser = this._userContextService.isViewingOtherUser;

	// Impersonation — when impersonating, user() is the impersonated user

	/**
	 * The admin behind an impersonation, or null when nobody is impersonating.
	 *
	 * From the impersonation context, NOT from `user().originalAdminUser`. Once the server resolves
	 * identity the account object IS the target's own record and carries no such field, so that read
	 * left the menu header with a blank name and a blank e-mail over an anonymous avatar — the one
	 * place whose whole job is to keep saying who you really are.
	 *
	 * Still nullable: an impersonation entered by putting `?userID=` on the URL is captured before
	 * the profile fetch, so there is no signed-in user yet to record as the actor. Unknown is then the
	 * honest answer, and the card below still names who is being acted as.
	 */
	public readonly impersonationActor = this._accountStateService.originalAdminUserID;
	public readonly originalAdminUserID = computed(() => this.impersonationActor()?.userID);
	public readonly impersonatedUserName = computed(() => {
		const u = this.user();
		return u ? `${u.firstName} ${u.lastName?.charAt(0)}.` : '';
	});

	// ── Derived header + view-context objects ──────────────────────────────

	/**
	 * Always the real logged-in user.
	 * When impersonating: the recorded actor (userID + username only), which may be unknown.
	 * Otherwise: own account.
	 */
	public readonly headerUser = computed<IUserMenuHeaderUser>(() => {
		if (this.isImpersonating()) {
			const admin = this.impersonationActor();
			return {
				userID: admin?.userID ?? 0,
				name: admin?.username ?? 'Your admin account',
				// The username IS the e-mail address, so repeating it here prints the same line twice.
				email: '',
				tzDisplay: this.selectedTzDisplay() ?? undefined,
				showTzWarning: this.showTzWarning(),
				browserTzDisplay: this.browserTzDisplay() ?? undefined
			};
		}
		const u = this.user();
		return {
			userID: u.userID,
			name: `${u.firstName} ${u.lastName}`,
			email: u.email ?? '',
			tzDisplay: this.selectedTzDisplay() ?? undefined,
			showTzWarning: this.showTzWarning(),
			browserTzDisplay: this.browserTzDisplay() ?? undefined
		};
	});

	/**
	 * Describes who is being viewed/impersonated, or null in standard mode.
	 * `isViewingOther` controls stacked avatars in the trigger.
	 */
	public readonly viewContext = computed<IUserMenuViewContext | null>(() => {
		if (this.isImpersonating()) {
			const u = this.user();
			return {
				type: 'impersonating',
				isViewingOther: true,
				primaryUserID: u.userID,
				primaryFirstName: u.firstName,
				primaryLastName: u.lastName ?? '',
				primaryEmail: u.email,
				primaryTzDisplay: this.impersonatedTzDisplay() ?? undefined,
				triggerLabel: `${u.preferredName ?? u.firstName} ${u.lastName ?? ''}`.trim(),
				sectionTitle: 'Impersonating'
			};
		}

		const mode = this.switchMode();
		if (mode !== 'none') {
			const v = this.viewingUser();
			return {
				type: mode as 'guardian' | 'institution',
				isViewingOther: this.isViewingOtherUser(),
				primaryUserID: v?.userID ?? this.viewingUserID() ?? 0,
				primaryFirstName: v?.firstName ?? '',
				primaryLastName: v?.lastName ?? '',
				primaryEmail: undefined,
				primaryTzDisplay: undefined,
				triggerLabel: v ? `${v.preferredName ?? v.firstName} ${v.lastName ?? ''}`.trim() : '',
				sectionTitle: 'View User'
			};
		}

		return null;
	});

	/**
	 * The impersonation half of {@link viewContext}, or null.
	 *
	 * Exists so the template can test it with a single `@if` around the projected
	 * `[md-menu-header]` element. Nesting a second `@if` inside the first to pick the impersonation
	 * branch stopped the block reaching the menu's header slot altogether, which took the whole
	 * section out of the popup. Selector-based projection is fragile about how deeply the matching
	 * element is buried in control flow, so each projected section gets exactly one condition.
	 */
	public readonly impersonationView = computed<IUserMenuViewContext | null>(() => {
		const ctx = this.viewContext();
		return ctx?.type === 'impersonating' ? ctx : null;
	});

	/**
	 * Whether the account-switching list belongs in the menu.
	 *
	 * Deliberately independent of {@link viewContext}, which answers a different question. That
	 * computed picks ONE of impersonation or switching, because it also drives the trigger avatar and
	 * only one face can be shown there. The list had been rendered inside its `@else`, so the two
	 * became mutually exclusive everywhere — and an admin impersonating a guardian got the guardian's
	 * identity but none of the guardian's learners, which is most of what a guardian account is for.
	 * The states genuinely compose: you are acting as someone, and that someone can switch between
	 * their own learners.
	 */
	public readonly showSwitchList = computed(() => this.switchMode() !== 'none' && this.associatedUsers().length > 0);

	/**
	 * Heading over the switch list. It has to name the relationship rather than reuse
	 * viewContext().sectionTitle, which now sits over the impersonation card above it — two sections
	 * headed "View User" would say nothing about which one switches what.
	 */
	public readonly switchListTitle = computed(() => (this.switchMode() === 'institution' ? 'Learners' : 'View Learner'));

	/** Filtered list for institution inline search, unfiltered for guardian. */
	public readonly institutionSearchTerm = signal<string>('');

	/** Show search + scroll when the list has more than 5 entries (any switch mode). */
	public readonly showSearch = computed(() => this.associatedUsers().length > 5);

	public readonly filteredAssociatedUsers = computed<IUserContextAssociatedUser[]>(() => {
		const users = this.associatedUsers();
		const term = this.institutionSearchTerm().toLowerCase().trim();
		if (!term) {
			return users;
		}
		return users.filter((u) => `${u.firstName} ${u.lastName}`.toLowerCase().includes(term));
	});

	public get userMenuOptions(): IMDMenuOption[] {
		const options: IMDMenuOption[] = [];
		const currentUser = this.user();

		options.push({
			label: 'Profile',
			icon: { type: 'url', icon: 'untitled-ui/users/user-01' },
			click: () => this.openProfile()
		});

		if (this.isAdmin()) {
			options.push({
				label: 'Update Timezone',
				icon: { type: 'url', icon: 'untitled-ui/time/clock-refresh' },
				class: this.headerUser().showTzWarning ? 'menu-item--tz-highlight' : undefined,
				click: () => this.openTimezoneDialog()
			});
		}

		options.push({ divider: true, label: '' });

		if (this._isInternalUser(currentUser)) {
			options.push({
				label: 'Mail',
				icon: { type: 'url', icon: 'untitled-ui/communication/mail-01' },
				link: 'https://outlook.office.com/mail/inbox/',
				linkTarget: '_blank'
			});
		}

		options.push(this._buildSupportMenuOption());

		options.push({ divider: true, label: '' });
		options.push({
			label: 'Logout',
			icon: { type: 'material', icon: 'logout' },
			click: () => this.logout()
		});

		return options;
	}

	public ngAfterViewChecked(): void {
		const list = this._switchList?.nativeElement;
		const isVisible = !!list && list.offsetParent !== null;
		const justOpened = isVisible && !this._wasListVisible;
		this._wasListVisible = isVisible;

		if (justOpened) {
			this._scrollActiveItemIntoView();
		}
	}

	private _scrollActiveItemIntoView(): void {
		const list = this._switchList?.nativeElement;
		if (!list) return;
		const active = list.querySelector<HTMLElement>('.user-switch-item--active');
		if (!active) return;

		// Manually adjust the container's scrollTop to avoid scrolling the page
		const itemTop = active.offsetTop;
		const itemBottom = itemTop + active.offsetHeight;
		const containerTop = list.scrollTop;
		const containerBottom = containerTop + list.clientHeight;

		if (itemTop < containerTop) {
			list.scrollTop = itemTop;
		} else if (itemBottom > containerBottom) {
			list.scrollTop = itemBottom - list.clientHeight;
		}
	}

	public handleSwitchUser(userID: number): void {
		this._menuPopup?.hide();
		this._userContextService.requestSwitchUser(userID);
	}

	public stopImpersonation(): void {
		this._menuPopup?.hide();
		this._accountStateService.stopImpersonation();
	}

	public onSearchInput(event: Event): void {
		this.institutionSearchTerm.set((event.target as HTMLInputElement).value);
	}

	public openProfile(): void {
		this._router.navigate(['profile'], { queryParams: { isCurrentUser: true }, onSameUrlNavigation: 'reload' });
	}

	public openTimezoneDialog(): void {
		const userID = this.user()?.userID;
		if (!userID) return;

		const dialogRef = this._dialogService.open(SelectTimezoneDialogComponent as Type<any>, { userID });

		dialogRef.afterClosed.subscribe((result: unknown) => {
			const selectedTimezone = result as ITimeZone | undefined;
			if (selectedTimezone?.timeZoneID) {
				this._globalTimezoneService.setUserSelectedTimezone(selectedTimezone);
			}
		});
	}

	public logout(): void {
		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<any>, {
			title: 'Confirm Logout',
			body: 'Are you sure you want to log out? You will need to sign in again to access your account.',
			icon: 'untitled-ui/arrows/log-out-01',
			successText: 'Logout'
		});

		dialogRef.afterClosed.subscribe((confirmed) => {
			if (confirmed) {
				this._authService.signOut();
			}
		});
	}

	private _buildSupportMenuOption(): IMDMenuOption {
		const user = this.user();
		const supportTicketUrl = buildSupportTicketUrl(user);
		const children: IMDMenuOption[] = [];

		if (this.isAdmin() && (this._appConfig.features?.viewKnowledgeBird ?? false)) {
			children.push({
				label: 'KnowledgeBird',
				icon: { type: 'url', icon: 'untitled-ui/education/graduation-hat-02' },
				link: 'https://knowledge.languagebird.com/',
				linkTarget: '_blank'
			});
		}

		children.push(
			{
				label: 'Help Center',
				icon: { type: 'url', icon: 'untitled-ui/education/book-open-01' },
				link: 'https://help.languagebird.com/',
				linkTarget: '_blank'
			},
			{
				label: 'Contact Support',
				icon: { type: 'url', icon: 'untitled-ui/general/life-buoy-01' },
				link: supportTicketUrl,
				linkTarget: '_blank'
			}
		);

		return {
			label: 'Support',
			icon: { type: 'url', icon: 'untitled-ui/communication/message-question-circle' },
			children
		};
	}

	private _isInternalUser(user: IUser | undefined): boolean {
		return !!user?.email?.toLowerCase().endsWith('@languagebird.com');
	}
}
